import Add16 from '@carbon/icons-react/es/add/16';
import Information16 from '@carbon/icons-react/es/information/16';
import { useSize } from 'ahooks';
import { Col, Row, Space, Typography } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { SalesforceIntegrationId } from 'api/integrations';
import { Button } from 'components/atoms/button';
import { EmptyState, EmptyStateContainer } from 'components/atoms/empty-state';
import { IconShell } from 'components/atoms/icon-shell';
import { PageLoading } from 'components/atoms/loading';
import { BlockSpace } from 'components/atoms/space';
import { DraggableTable } from 'components/molecules/table';
import { getSalesforceNewAutomationUrl } from 'utils/urls';
import { generateColumns } from './generate-columns';
import { useAutomationsListActions } from './use-automation-list-actions';

const Table = styled(DraggableTable)`
  .ant-table-thead > tr > th {
    padding: 0 var(--spacing-03);
  }
`;

const { Text } = Typography;

export const AutomationList = () => {
  const { integrationId } = useParams<SalesforceIntegrationId>();
  const {
    automationList,
    automationListStatus,
    onSortChange,
    onChange,
    onDelete,
    onEdit,
  } = useAutomationsListActions();

  const columns = generateColumns({
    integrationId,
    handleDelete: onDelete,
    handleEnable: onChange,
    handleEdit: onEdit,
  });

  const { height } = useSize(window.document.body);
  const scroll = {
    y: Math.max(height! - 270),
  };

  switch (automationListStatus) {
    case 'loading': {
      return <PageLoading />;
    }
    case 'success': {
      return automationList?.length ? (
        <BlockSpace direction="vertical" size="small">
          <Row justify="space-between" align="middle" gutter={16}>
            <Col>
              <Text>
                <Space align="center">
                  <IconShell icon={<Information16 />} />
                  <FormattedMessage id="settings.integrations.sf.automation.list.helper" />
                </Space>
              </Text>
            </Col>
            <Col>
              <Link to={getSalesforceNewAutomationUrl(integrationId)}>
                <Button type="primary" icon={<Add16 />} iconPlacement="right">
                  <FormattedMessage id="settings.integrations.sf.automation.add" />
                </Button>
              </Link>
            </Col>
          </Row>
          <Table
            scroll={scroll}
            onSortChange={onSortChange}
            dataSource={automationList}
            columns={columns}
            pagination={false}
            rowKey="automationId"
            size="small"
          />
        </BlockSpace>
      ) : (
        <EmptyStateContainer>
          <EmptyState
            title={
              <FormattedMessage id="settings.integrations.sf.automation.empty.title" />
            }
            message={
              <FormattedMessage id="settings.integrations.sf.automation.empty.message" />
            }
            primaryCTA={
              <Link to={getSalesforceNewAutomationUrl(integrationId)}>
                <EmptyState.CallToAction.Primary icon={<Add16 />}>
                  <FormattedMessage id="settings.integrations.sf.automation.add" />
                </EmptyState.CallToAction.Primary>
              </Link>
            }
          />
        </EmptyStateContainer>
      );
    }
    case 'error':
    case 'idle':
    default:
      return null;
  }
};
